/**
 * Reconstruct nested token-usage maps from either real nested objects or
 * legacy dotted field names written by `set({ merge })` (e.g. `monthlyAiCost.2026-09`).
 *
 * Firestore `set` with dotted keys stores a literal field named `monthlyAiCost.2026-09`.
 * `update` with dotted keys writes a nested map. Readers must accept both.
 */
export const MODEL_COST_METRIC_KEYS = [
    "promptTokens",
    "completionTokens",
    "costUsd",
    "callCount",
    "fallbackCount",
];
export const OPERATION_STAT_METRIC_KEYS = ["promptTokens", "completionTokens", "callCount"];
function asNumber(value) {
    return typeof value === "number" && Number.isFinite(value) ? value : 0;
}
function mergeNumericMap(target, extra) {
    if (!extra || typeof extra !== "object")
        return;
    for (const [key, value] of Object.entries(extra)) {
        if (typeof value === "number" && Number.isFinite(value))
            target[key] = value;
    }
}
function mergeNestedNumeric(target, extra) {
    if (!extra || typeof extra !== "object")
        return;
    for (const [key, value] of Object.entries(extra)) {
        if (!value || typeof value !== "object")
            continue;
        target[key] ??= {};
        for (const [metric, metricValue] of Object.entries(value)) {
            if (typeof metricValue === "number" && Number.isFinite(metricValue)) {
                target[key][metric] = metricValue;
            }
        }
    }
}
const MODEL_METRIC_SET = new Set(MODEL_COST_METRIC_KEYS);
function isPlainObject(value) {
    return value != null && typeof value === "object" && !Array.isArray(value);
}
/**
 * `update("modelCosts.<id>.callCount")` splits every `.` in the model id.
 * `google_gemini-2.5-flash-lite` lands as
 * `{ "google_gemini-2": { "5-flash-lite": { callCount } } }`.
 * Walk until a metric bag and rejoin the segments. Sum when the canonical
 * key already exists (old split tree + a later FieldPath write).
 * Do not create empty objects — Overview calls `callCount.toLocaleString()`.
 */
function mergeModelCosts(target, extra) {
    if (!isPlainObject(extra))
        return;
    const walk = (node, prefix) => {
        const entries = Object.entries(node);
        const hasMetric = entries.some(([key, value]) => MODEL_METRIC_SET.has(key) && typeof value === "number" && Number.isFinite(value));
        if (prefix && hasMetric) {
            target[prefix] ??= {};
            for (const [metric, metricValue] of entries) {
                if (typeof metricValue === "number" && Number.isFinite(metricValue)) {
                    target[prefix][metric] = (target[prefix][metric] ?? 0) + metricValue;
                }
            }
        }
        for (const [key, value] of entries) {
            if (MODEL_METRIC_SET.has(key) || !isPlainObject(value))
                continue;
            walk(value, prefix ? `${prefix}.${key}` : key);
        }
    };
    walk(extra, "");
}
/** Parse a raw Firestore token-usage document (admin or per-user). */
export function parseTokenUsageMaps(raw) {
    const monthlyAiCost = {};
    const operationCounts = {};
    const operationStats = {};
    const modelCosts = {};
    const dottedKeys = [];
    if (!raw) {
        return { monthlyAiCost, operationCounts, operationStats, modelCosts, dottedKeys };
    }
    mergeNumericMap(monthlyAiCost, raw.monthlyAiCost);
    mergeNumericMap(operationCounts, raw.operationCounts);
    mergeNestedNumeric(operationStats, raw.operationStats);
    mergeModelCosts(modelCosts, raw.modelCosts);
    const modelMetricSet = new Set(MODEL_COST_METRIC_KEYS);
    const opStatSet = new Set(OPERATION_STAT_METRIC_KEYS);
    for (const [key, value] of Object.entries(raw)) {
        let match = key.match(/^monthlyAiCost\.(.+)$/);
        if (match) {
            monthlyAiCost[match[1]] = asNumber(value);
            dottedKeys.push(key);
            continue;
        }
        match = key.match(/^operationCounts\.(.+)$/);
        if (match) {
            operationCounts[match[1]] = asNumber(value);
            dottedKeys.push(key);
            continue;
        }
        match = key.match(/^operationStats\.(.+)\.(promptTokens|completionTokens|callCount)$/);
        if (match) {
            const op = match[1];
            const metric = match[2];
            if (!opStatSet.has(metric))
                continue;
            operationStats[op] ??= {};
            operationStats[op][metric] = asNumber(value);
            dottedKeys.push(key);
            continue;
        }
        match = key.match(/^modelCosts\.(.+)\.(promptTokens|completionTokens|costUsd|callCount|fallbackCount)$/);
        if (match) {
            const modelKey = match[1];
            const metric = match[2];
            if (!modelMetricSet.has(metric))
                continue;
            modelCosts[modelKey] ??= {};
            modelCosts[modelKey][metric] = asNumber(value);
            dottedKeys.push(key);
        }
    }
    return { monthlyAiCost, operationCounts, operationStats, modelCosts, dottedKeys };
}
export function utcMonthKey(date = new Date()) {
    return date.toISOString().slice(0, 7);
}
export function utcDateKey(date = new Date()) {
    return date.toISOString().slice(0, 10);
}
/**
 * Look up a UTC month in the AI cost map without treating absence as $0.
 * The in-progress UTC month is always `incomplete` even when a value exists.
 */
export function resolveMonthCost(monthlyAiCost, monthKey, now = new Date()) {
    const currentMonth = utcMonthKey(now);
    const raw = monthlyAiCost?.[monthKey];
    if (raw == null || !Number.isFinite(raw)) {
        return { value: null, completeness: "missing" };
    }
    if (monthKey >= currentMonth) {
        return { value: raw, completeness: "incomplete" };
    }
    return { value: raw, completeness: "complete" };
}
/** Build a nested Firestore payload from a parsed map (no dotted keys). */
export function nestedTokenUsageWrite(parsed) {
    return {
        monthlyAiCost: parsed.monthlyAiCost,
        operationCounts: parsed.operationCounts,
        operationStats: parsed.operationStats,
        modelCosts: parsed.modelCosts,
    };
}
//# sourceMappingURL=token-usage-maps.js.map